'use client'
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Image } from 'components/Image'
import classNames from 'classnames'
import { Button } from 'components/Button'
import { Typography } from 'components/Typography'
import { AchieveLinkNoMui } from 'components/AchieveLinkNoMui/AchieveLinkNoMui'
import FooterContainer from '../../components/Footer/FooterContainer/FooterContainer'
import { getLoginEmailError, isValidLoginEmail } from '../../utils/loginEmail/loginEmail'
import LoginTrustStrip from './LoginTrustStrip'
import Upscope from './Upscope'
import { useAnalyticsTrackEvent } from 'hooks/useAnalyticsTrackEvent'

const OVERLAY_FOOTER_DESKTOP_MQ = '(min-width: 1280px)'

const loginComp = {
  title: 'Client Dashboard Login',
  sub: 'You can use your Freedom Debt Relief account to log into your dashboard.',
  emailLabel: 'Enter your email',
  continue: 'Continue',
  notClientLine: 'Not a client? Your first step toward a debt-free future starts here.',
  explore: 'Explore your options',
}
const data = {
  clients: `Over&nbsp;
            <span class="font-bold">1 million</span>
                  &nbsp;clients enrolled`,
  noRisk: `Free,&nbsp;
              <span class="font-bold">no-risk</span>
                    &nbsp;credit card debt relief consultation`,
  debtResolved: `Over&nbsp;
            <span class="font-extrabold">$20 billion</span>
                        &nbsp;debt resolved since 2002`,
}
const ClientDashboardLoginOverlay = ({
  onClose,
  footerContainerProps,
  openedFromShowFormCookie = false,
  applyFdrUrl,
  upscopeUrl,
}) => {
  const [email, setEmail] = useState('')
  const [touched, setTouched] = useState(false)
  const [footerInContentShell, setFooterInContentShell] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(OVERLAY_FOOTER_DESKTOP_MQ).matches : false
  )
  const { analyticsTrackEvent } = useAnalyticsTrackEvent()
  const emailInputRef = useRef(null)
  const hasFiredInputClick = useRef(false)

  useLayoutEffect(() => {
    const frameId = window.requestAnimationFrame(() => {
      emailInputRef.current?.focus({ preventScroll: true })
    })

    return () => window.cancelAnimationFrame(frameId)
  }, [])

  useEffect(() => {
    const mq = window.matchMedia(OVERLAY_FOOTER_DESKTOP_MQ)
    const sync = () => setFooterInContentShell(mq.matches)
    mq.addEventListener('change', sync)

    return () => mq.removeEventListener('change', sync)
  }, [])

  useEffect(() => {
    document.body.classList.add('overflow-hidden')

    return () => document.body.classList.remove('overflow-hidden')
  }, [])

  useEffect(() => {
    // Login overlay view — fires whenever the overlay is displayed
    analyticsTrackEvent({
      element_name: 'login-overlay',
      nav_link_section: 'Login Overlay',
      track_event: 'login_view',
    })
  }, [analyticsTrackEvent])

  const errorMessage = getLoginEmailError(email, { touched })
  const showError = Boolean(errorMessage)
  const hasInput = email.trim().length > 0

  const pageContentShell = classNames(
    'mx-auto min-w-0 w-full',
    'px-4 md:px-5 xl:px-20',
    'w-content'
  )

  const overlayFooter = footerContainerProps ? <FooterContainer {...footerContainerProps} /> : null

  const handleExploreOptionsClick = useCallback(
    (e) => {
      e.preventDefault()
      analyticsTrackEvent({
        click_text: 'Explore your options',
        click_type: 'Link Click',
        flow_name: 'Signin',
        flow_step_name: 'Signin | Login: Explore your options Clicked',
        track_event: 'Signin Explore',
      })
      window.location.href = applyFdrUrl
    },
    [analyticsTrackEvent, applyFdrUrl]
  )

  const trackInputClick = useCallback(() => {
    analyticsTrackEvent({
      flow_event: 'Lead CTA Click',
      flow_name: 'Signin',
      flow_step_name: 'Signin | Login: Input Click',
      nav_link_section: 'Form',
      track_event: 'form_view',
    })
  }, [analyticsTrackEvent])

  const handleEmailFocus = useCallback(() => {
    if (hasFiredInputClick.current) return
    hasFiredInputClick.current = true
    trackInputClick()
  }, [trackInputClick])

  const handleEmailClick = useCallback(() => {
    trackInputClick()
  }, [trackInputClick])

  const handleEmailChange = useCallback(
    (e) => {
      setEmail(e.target.value)
      if (showError) setTouched(false)
    },
    [showError]
  )

  const handleContinue = useCallback(
    async (e) => {
      e.preventDefault()
      setTouched(true)
      if (!isValidLoginEmail(email)) return
      const hashBuffer = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(email))
      const form_email_hash = Array.from(new Uint8Array(hashBuffer))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('')
      analyticsTrackEvent({
        click_text: 'Continue',
        flow_event: 'Lead CTA Click',
        flow_name: 'Signin',
        flow_step_name: 'Signin | Login: Button Click',
        form_email: email,
        form_email_hash,
        nav_link_section: 'Form',
        track_event: 'form_view',
      })
      const currentParams = new URLSearchParams(window.location.search)
      currentParams.set('email', email)
      window.location.href = `/login?${currentParams.toString()}`
    },
    [analyticsTrackEvent, email]
  )

  return (
    <div
      className="fixed inset-0 z-220 flex animate-clientDashboardLoginOverlay flex-col overflow-y-auto bg-white"
      role="dialog"
      aria-modal="true"
      aria-labelledby="client-dashboard-login-title"
      data-testid="client-dashboard-login-overlay"
    >
      <Upscope upscopeUrl={upscopeUrl} />
      <div className={classNames('shrink-0 bg-white', pageContentShell)}>
        <div className={classNames('mx-auto flex w-full max-w-[327px] justify-center py-2')}>
          <AchieveLinkNoMui
            href="/"
            className="inline-flex h-12 shrink-0 items-center"
            onClick={(e) => {
              e.preventDefault()
              onClose()
            }}
          >
            <Image
              src="/multi-site-assets/fdr-logo.svg"
              alt="Freedom Debt Relief"
              className="h-12 w-auto max-w-[200px]"
              width={166}
              height={48}
            />
          </AchieveLinkNoMui>
        </div>
      </div>
      <div className={classNames('shrink-0 bg-white', pageContentShell)} aria-hidden>
        <div className="h-px w-full min-w-0 bg-[#E8E8E8]" />
      </div>
      {openedFromShowFormCookie ? null : (
        <div className={classNames('shrink-0 bg-white', pageContentShell)}>
          <div className={classNames('mx-auto w-full max-w-[327px]', 'flex justify-end pt-2 pb-0')}>
            <Button
              defaultbutton
              type="button"
              className="size-6 flex shrink-0 items-center justify-center border-0 bg-transparent p-0 opacity-90 hover:opacity-100 cursor-pointer"
              aria-label="Close"
              onClick={onClose}
              data-testid="client-dashboard-login-close"
            >
              <Image
                src="/client-dashboard-login/close.svg"
                alt=""
                className="size-6"
                width={24}
                height={24}
              />
            </Button>
          </div>
        </div>
      )}

      <div className="flex w-full min-w-0 flex-1 flex-col bg-white font-ultramarine">
        <div
          className={classNames(
            pageContentShell,
            'min-w-0 pb-12',
            openedFromShowFormCookie ? 'pt-10 md:pt-12' : 'pt-4'
          )}
        >
          <div
            className={classNames('mx-auto w-full min-w-0 xl:max-w-(--breakpoint-lg)', 'min-w-0')}
          >
            <form className="flex flex-col items-center gap-6" onSubmit={handleContinue} noValidate>
              <div className="flex w-full max-w-[295px] flex-col gap-2 text-center">
                <Typography
                  variant="headingMd"
                  component="h2"
                  id="client-dashboard-login-title"
                  data-testid="email-form-welcome-back-title"
                  className="m-0 text-2xl font-bold leading-[1.3] text-[#154199]"
                >
                  {loginComp.title}
                </Typography>
                <Typography
                  variant="bodySm"
                  component="p"
                  data-testid="sign-in-subtitle"
                  className="m-0 text-sm font-normal leading-[1.4] text-[#2F2F2F]"
                >
                  {loginComp.sub}
                </Typography>
              </div>

              <div className="flex w-full max-w-[303px] flex-col gap-6">
                <div className="relative w-full shrink-0">
                  <div className="relative h-[65px] w-full shrink-0">
                    <Typography
                      variant="bodySm"
                      component="label"
                      htmlFor="client-dashboard-login-email"
                      className={classNames(
                        'pointer-events-none absolute left-[11px] top-[9px] z-2 -translate-y-1/2 bg-white px-1 text-[12px] font-normal leading-[1.4]',
                        showError ? 'text-[#D02529]' : 'text-[#154199]'
                      )}
                    >
                      {loginComp.emailLabel}
                    </Typography>
                    <div
                      className={classNames(
                        'absolute left-0 top-[9px] flex h-[56px] w-full items-center rounded-[8px] border border-solid bg-white px-4 shadow-none transition-colors',
                        showError
                          ? 'border-[#CB000E] focus-within:border-[#CB000E]'
                          : 'border-[#154199] focus-within:border-[#154199]'
                      )}
                      onMouseDown={(e) => {
                        if (e.target !== emailInputRef.current) {
                          e.preventDefault()
                          emailInputRef.current?.focus({ preventScroll: true })
                        }
                      }}
                    >
                      <input
                        ref={emailInputRef}
                        id="client-dashboard-login-email"
                        data-testid="email-form-sign-in"
                        name="email"
                        type="email"
                        autoComplete="email"
                        value={email}
                        onFocus={handleEmailFocus}
                        onClick={handleEmailClick}
                        onChange={handleEmailChange}
                        className="min-h-0 w-full min-w-0 flex-1 border-0 bg-transparent p-0 font-ultramarine text-[16px] font-normal leading-[1.4] text-[#2F2F2F] outline-hidden ring-0 focus:ring-0"
                        aria-invalid={showError}
                        aria-describedby={
                          showError ? 'client-dashboard-login-email-error' : undefined
                        }
                      />
                    </div>
                  </div>
                  {showError ? (
                    <Typography
                      variant="bodySm"
                      component="p"
                      id="client-dashboard-login-email-error"
                      role="alert"
                      className="mb-0 mt-2 text-sm font-normal leading-[1.4] text-[#D02529]"
                    >
                      {errorMessage}
                    </Typography>
                  ) : null}
                </div>

                <Button
                  defaultbutton
                  type="submit"
                  disabled={!hasInput}
                  data-testid="email-form-continue-button"
                  className={classNames(
                    'min-h-12 flex w-full items-center justify-center border-0 rounded-[8px] px-6 py-2 text-[18px] font-bold leading-[1.2] tracking-[0.36px] transition-colors',
                    hasInput
                      ? 'cursor-pointer bg-[#154199] text-[#F9F9F9] hover:bg-blue-600'
                      : 'cursor-not-allowed bg-[#F3F3F3] text-[#C4C4C4]'
                  )}
                >
                  {loginComp.continue}
                </Button>
              </div>
            </form>

            <div className="mt-12 flex flex-col items-center gap-8">
              <div className="w-full max-w-[327px] rounded-[10px] border border-solid border-[#C4C4C4] bg-white px-3 py-5">
                <div className="flex gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center">
                    <Image
                      src="/client-dashboard-login/not-client-magnify-dollar.svg"
                      alt=""
                      className="h-10 max-h-10 w-[35px] object-contain object-left"
                      width={35}
                      height={40}
                    />
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col gap-2">
                    <Typography
                      variant="bodySm"
                      component="p"
                      className="m-0 text-sm font-normal leading-[1.4] text-[#2F2F2F]"
                    >
                      {loginComp.notClientLine}
                    </Typography>
                    <Button
                      defaultbutton
                      type="button"
                      className="inline-flex w-fit items-center gap-1 py-2 border-0 bg-transparent text-sm font-bold leading-[1.2] tracking-[0.28px] text-[#154199] cursor-pointer hover:underline"
                      onClick={handleExploreOptionsClick}
                    >
                      {loginComp.explore}
                      <Image
                        src="/client-dashboard-login/explore-arrow.svg"
                        alt=""
                        className="size-4 shrink-0"
                        width={16}
                        height={16}
                      />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 w-full min-w-0">
            <LoginTrustStrip iconStrip={data} />
          </div>
          {footerInContentShell ? overlayFooter : null}
        </div>

        {footerInContentShell ? null : overlayFooter}
      </div>
    </div>
  )
}

export default ClientDashboardLoginOverlay
